import React from 'react';

import { FIELDS, MAX_RESULTS } from '@lumapps/content-templates/components/TemplateMenu/constants';
import { useContentTemplatesQuery } from '@lumapps/content-templates/hooks/useContentTemplatesQuery';
import { CONTENT_TYPES } from '@lumapps/content-types/keys';
import { spaceContentCreate } from '@lumapps/contents/routes/admin';
import { useDataAttributes } from '@lumapps/data-attributes';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { mdiTablet, mdiViewQuilt } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { Link as RouterLink } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { InfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';

export interface SpaceContentTemplateMenuProps {
    /** Id of the space */
    spaceId: string;
    /** Id of the content type owning the templates */
    contentTypeId: string;
    /** whether the default template option is enabled for this type of template */
    defaultTemplateEnabled: boolean;
    /** scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
    /** Whether this content type should open in writer mode (writer-only users) */
    shouldOpenWriterEditor?: boolean;
}

/**
 * Menu of all available content templates to create content from within a space context.
 * Routes to space-specific content creation URLs.
 */
export const SpaceContentTemplateMenu: React.FC<SpaceContentTemplateMenuProps> = (props) => {
    const { spaceId, contentTypeId, defaultTemplateEnabled, scope, shouldOpenWriterEditor = false } = props;
    const { get } = useDataAttributes(scope);
    const { translateKey, translateObject } = useTranslate();

    const lang = useSelector(currentLanguageSelector);
    const instance = useSelector(instanceIdSelector);

    const params = React.useMemo(
        () => ({
            fields: FIELDS,
            customContentType: contentTypeId,
            instance,
            lang,
            maxResults: MAX_RESULTS,
            showTemplatesWithInheritedOnlyParam: true,
        }),
        [contentTypeId, instance, lang],
    );

    const {
        items: templateList,
        hasNextPage,
        isInitialLoading,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
    } = useContentTemplatesQuery(params, { enabled: true, cacheTime: Infinity, staleTime: Infinity });

    if (isInitialLoading) {
        return <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />;
    }

    return (
        <>
            {templateList.map((template) => (
                <Menu.Item
                    key={template.id}
                    icon={mdiViewQuilt}
                    as={RouterLink}
                    to={spaceContentCreate({
                        spaceId,
                        type: 'custom',
                        customContentType: contentTypeId,
                        templateId: template.id,
                        shouldOpenWriterEditor,
                    })}
                    {...get({ element: contentTypeId, action: 'template', type: template.id })}
                >
                    {translateObject(template.name)}
                </Menu.Item>
            ))}

            {isFetchingNextPage && (
                <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />
            )}

            {defaultTemplateEnabled && templateList?.length > 0 && <Menu.Divider />}

            {defaultTemplateEnabled && (
                <Menu.Item
                    icon={mdiTablet}
                    as={RouterLink}
                    to={spaceContentCreate({
                        spaceId,
                        type: 'custom',
                        customContentType: contentTypeId,
                        shouldOpenWriterEditor,
                    })}
                    {...get({ element: contentTypeId, action: 'template', type: 'empty' })}
                >
                    {translateKey(CONTENT_TYPES.CREATE_FROM_SCRATCH)}
                </Menu.Item>
            )}

            {hasNextPage && !isLoading && <InfiniteScroll callback={() => fetchNextPage()} />}
        </>
    );
};
